/**
 * Privacy mode (BUILD-PLAN 2.6, M01): Shift+P blurs patient-identifying
 * text for a moment (a patient walks up to the desk, a screen is shared).
 *
 * - Elements marked `data-phi` are blurred by CSS while it is on.
 * - Text the shell renders itself (palette rows, toasts, document title) is
 *   replaced, not blurred: `maskName()` turns a name into initials.
 * - It lasts for this browser tab only (session storage), so a shared
 *   workstation does not stay masked for the next person by surprise.
 *
 * The server-side mode that never sends names (waiting-room screens) is a
 * device-profile setting owned by WP0-AUTH.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { readString, writeString } from '../lib/storage.js';

const KEY = 'velora.privacy';

interface PrivacyValue {
  on: boolean;
  setOn: (on: boolean) => void;
  toggle: () => void;
}

const PrivacyContext = createContext<PrivacyValue>({
  on: false,
  setOn: () => undefined,
  toggle: () => undefined,
});

/** "Jordan Testcase" -> "J. T."; empty input stays empty. */
export function maskName(name: string): string {
  const parts = name.replace(/[(),]/g, ' ').trim().split(/\s+/).filter(Boolean);
  return parts.map((p) => `${p[0]?.toUpperCase() ?? ''}.`).join(' ');
}

/** Mirrors the state on <html data-privacy> so CSS can blur `[data-phi]`. */
export function applyPrivacyAttribute(
  on: boolean,
  el: HTMLElement = document.documentElement,
): void {
  if (on) el.setAttribute('data-privacy', 'on');
  else el.removeAttribute('data-privacy');
}

export function PrivacyProvider({ children }: { children: ReactNode }) {
  const [on, setOnState] = useState(() => readString(KEY, 'session') === 'on');

  useEffect(() => {
    applyPrivacyAttribute(on);
    writeString(KEY, on ? 'on' : null, 'session');
  }, [on]);

  const setOn = useCallback((next: boolean) => setOnState(next), []);
  const toggle = useCallback(() => setOnState((v) => !v), []);
  const value = useMemo(() => ({ on, setOn, toggle }), [on, setOn, toggle]);
  return <PrivacyContext.Provider value={value}>{children}</PrivacyContext.Provider>;
}

export function usePrivacy(): PrivacyValue & { mask: (text: string) => string } {
  const ctx = useContext(PrivacyContext);
  return { ...ctx, mask: (text: string) => (ctx.on ? maskName(text) : text) };
}

/**
 * Sets `document.title`. Pass `phi: true` when the title names a patient:
 * privacy mode then shows initials instead.
 */
export function useDocumentTitle(
  title: string,
  options: { phi?: boolean; suffix?: string } = {},
): void {
  const { on } = useContext(PrivacyContext);
  const { phi = false, suffix } = options;
  useEffect(() => {
    const shown = phi && on ? maskName(title) : title;
    document.title = suffix ? `${shown} - ${suffix}` : shown;
  }, [title, phi, on, suffix]);
}
